import theme from "theme";
import { useTranslation } from "react-i18next";
import { Box, Grid, Typography } from "@mui/material";
import {
  Speed,
  Shield,
  Diamond,
  AutoAwesome,
} from "@mui/icons-material"
import Button from "components/atoms/Button";
import { useNavigate } from "react-router-dom";
import eventYeloBanner from "assets/services/eventYeloDiffer.webp";

const YeloDifference = () => {
const {t,i18n}=useTranslation("")
const navigate=useNavigate()
const whyChooseUs = [
  {
    icon: <Shield sx={{ fontSize: 32 }} />,
    title: t("professionalChauffeurs"),
    description: t("professionalChauffeursDetails"),
    stat: "100%",
    statLabel: "Licensed",
  },
  {
    icon: <Diamond sx={{ fontSize: 32 }} />,
    title: t("luxuryFleet"),
    description: t("luxuryFleetDetails"),
    stat: "50+",
    statLabel: "Vehicles",
  },
  {
    icon: <Speed sx={{ fontSize: 32 }} />,
    title: t("punctuality"),
    description: t("punctualityDetails"),
    stat: "99%",
    statLabel: "On-Time",
  },
  {
    icon: <AutoAwesome sx={{ fontSize: 32 }} />,
    title: t("support"),
    description: t("supportDetails"),
    stat: "24/7",
    statLabel: "Available",
  },
]

return (
    <>
    <Box sx={{ textAlign: "center", mb: { xs: 8, md: 10 },mt:15 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 3 }}>
        <Box sx={{ width: 60, height: 2, bgcolor: "#FFCD00", mr: 2 }} />
        <Box
          sx={{
            bgcolor: "rgba(82, 10, 118, 0.1)",
            color:theme.palette.primary.contrastText,
            px: 3,
            py: 1,
            borderRadius: 5,
            fontWeight: 600,
            fontSize: "0.9rem",
          }}
        >
          {t("choose")}
        </Box>
        <Box sx={{ width: 60, height: 2, bgcolor: "#FFCD00", ml: 2 }} />
      </Box>

      <Typography
        variant="h2"
        sx={{
          fontSize: { xs: "2.5rem", md: "2.5rem" },
          color:theme.palette.primary.contrastText,
          mb: 4,
          fontWeight: 800,
          lineHeight: 1.2,
        }}
      >
        {t("theYeloDifference")}
      </Typography>

      <Typography
        variant="h6"
        sx={{
          color: "text.secondary",
          fontWeight: 400,
          lineHeight: 1.7,
          fontSize: "1rem",
          maxWidth: 700,
          mx: "auto",
        }}
      >
        {t("yeloDifferenceDetails")}
      </Typography>
    </Box>

    <Grid container spacing={8}>
      <Grid item xs={12} md={6} order={{ xs: 1, md: 1 }}>
        <Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {whyChooseUs.map((feature, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  p: 3,
                  borderRadius: 4,
                  bgcolor: "white",
                  border: "1px solid rgba(82, 10, 118, 0.08)",
                  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.05)",
                  transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 0,
                    height: "100%",
                    width: "4px",
                    background: "linear-gradient(to bottom, #FFCD00, #520A76)",
                    opacity: 0,
                    transition: "opacity 0.3s ease",
                  },
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    right: -40,
                    bottom: -40,
                    width: 120,
                    height: 120,
                    borderRadius: "50%",
                    background: `radial-gradient(circle, rgba(255,205,0,0.1) 0%, rgba(255,255,255,0) 70%)`,
                    zIndex: 0,
                  },
                  "&:hover": {
                    boxShadow: "0 15px 40px rgba(82, 10, 118, 0.15)",
                    transform: "translateY(-5px)",
                    borderColor: "rgba(82, 10, 118, 0.2)",
                    "&::before": {
                      opacity: 1,
                    },
                    "& .feature-title": {
                      color: "#520A76",
                      transform: "translateX(5px)",
                    },
                    "& .feature-number": {
                      opacity: 0.2,
                      transform: "translateX(0)",
                    },
                  },
                }}
              >
                <Box
                  className="feature-number"
                  sx={{
                    position: "absolute",
                    top: 10,
                    right: 15,
                    fontSize: "4rem",
                    fontWeight: 900,
                    color: "rgba(82, 10, 118, 0.03)",
                    lineHeight: 1,
                    opacity: 0.1,
                    transform: "translateX(20px)",
                    transition: "all 0.4s ease",
                  }}
                >
                  {(index + 1).toString().padStart(2, "0")}
                </Box>
                <Box
                  className="feature-icon-wrapper"
                  sx={{
                    mr: 3,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 50,
                    height: 50,
                    borderRadius: "15px",
                    background:
                      "linear-gradient(135deg, rgba(82, 10, 118, 0.05) 0%, rgba(82, 10, 118, 0.1) 100%)",
                      color:theme.palette.primary.contrastText,
                      flexShrink: 0,
                    boxShadow: "0 8px 20px rgba(82, 10, 118, 0.1)",
                    transition: "all 0.3s ease",
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      inset: -3,
                      borderRadius: "23px",
                      border: "1px solid rgba(82, 10, 118, 0.1)",
                      opacity: 0.5,
                    },
                  }}
                >
                  {feature.icon}
                </Box>

                <Box sx={{ flex: 1, position: "relative", zIndex: 1 }}>
                  <Typography
                    className="feature-title"
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color:theme.palette.primary.contrastText,
                      mb: 1.5,
                      fontSize: "1.2rem",
                      letterSpacing: "-0.01em",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {feature.title}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: "text.secondary",
                      lineHeight: 1.3,
                      fontSize: "1rem",
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
          <Button
              variant="contained"
              size="large"
              color="secondary"
              onClick={()=> navigate("/add-event")}
              sx={{
                "&:hover": {
                  transform: "translateY(-3px)",
                },
                px:3,
                py:3,
                mt:6,
                fontWeight: 500,
                height: "42px",
                borderRadius: "12px",
                textTransform: "capitalize",
                color:"white",
                fontSize: "1.19rem",
                boxShadow: "0 4px 10px rgba(0,0,0,0.3)",
                transition: "all 0.3s ease",
              }}
            >
              {t("getAQuote")}
             </Button>
          </Box>
      </Grid>
      <Grid item xs={12} md={6} order={{ xs: 2, md: 2 }}>
        <Box
          sx={{
            position: "relative",
            height: "70%",
            minHeight: 500,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "auto",
              maxWidth: 500,
              borderRadius: 4,
              overflow: "hidden",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.2)",
              position: "relative",
              zIndex: 2,
              border: "5px solid white",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "linear-gradient(180deg, rgba(82, 10, 118, 0) 0%, rgba(82, 10, 118, 0.4) 100%)",
                zIndex: 1,
                opacity: 0.7,
                transition: "opacity 0.5s ease",
              },
              "&:hover": {
                "&::before": {
                  opacity: 0.3,
                },
                "& img": {
                  transform: "scale(1.05)",
                },
                "& .image-badge": {
                  transform: "translateY(0)",
                  opacity: 1,
                },
              },
            }}
          >
            <Box
              component="img"
              src={eventYeloBanner}
              alt="Premium Transportation Service"
              sx={{
                width: "100%",
                height: "auto",
                display: "block",
                transition: "transform 0.8s ease",
              }}
            />

            <Box
              className="image-badge"
              sx={{
                position: "absolute",
                bottom: 30,
                right: 30,
                bgcolor: "rgba(255, 255, 255, 0.95)",
                borderRadius: 2,
                boxShadow: "0 10px 30px rgba(0, 0, 0, 0.15)",
                p: 2,
                zIndex: 3,
                display: "flex",
                alignItems: "center",
                gap: 2,
                transform: "translateY(10px)",
                opacity: 0.9,
                transition: "all 0.5s ease",
              }}
            >
              <Box
                sx={{
                  bgcolor: "rgba(82, 10, 118, 0.1)",
                  borderRadius: "50%",
                  width: 35,
                  height: 35,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#520A76",
                }}
              >
                <Diamond sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.8rem",
                  }}
                >
                  {t("premiumFleet")}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#520A76",
                    fontWeight: 700,
                    fontSize: "1.1rem",
                  }}
                >
                  {t("luxuryExperience")}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              position: "absolute",
              top: -20,
              left: -20,
              width: 120,
              height: 120,
              borderRadius: "50%",
              border: "4px solid #FFCD00",
              opacity: 0.2,
              zIndex: 1,
              animation: "pulse 4s infinite ease-in-out",
              "@keyframes pulse": {
                "0%": { transform: "scale(1)" },
                "50%": { transform: "scale(1.05)" },
                "100%": { transform: "scale(1)" },
              },
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -30,
              right: -30,
              width: 180,
              height: 180,
              borderRadius: "50%",
              border: "4px solid #520A76",
              opacity: 0.1,
              zIndex: 1,
              animation: "pulse 4s infinite ease-in-out 1s",
            }}
          />
        </Box>
      </Grid>
    </Grid>
    </>
  );
};

export default YeloDifference;
